/**
 * WalletScreen v2 — Phantom-style deep dark design
 * Drop-in replacement for apps/mobile/src/screens/WalletScreen.tsx
 *
 * Changes vs v1:
 * - Radial glow behind balance block
 * - btn-primary gets box-shadow glow
 * - Token cards use gradient letter icons
 * - Address button styled as pill
 * - Tab underlines updated
 * - card class uses new border style
 * ALL business logic, hooks, store calls — unchanged.
 */

import { useEffect, useRef, useState } from 'react'
import { formatAccountDisplay } from '@properwallet/wallet-core'
import { formatUsd, sortTokensForDisplay } from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { useTranslation } from '../hooks/useTranslation'
import BottomNav from '../components/BottomNav'
import TokenCard from '../components/TokenCard'
import ResourceBar from '../components/ResourceBar'
import NftGallery from '../components/NftGallery'
import NftSendModal from '../components/NftSendModal'
import HistoryList from '../components/HistoryList'
import AccountSwitcherModal from '../components/AccountSwitcherModal'
import ReceiveModal from '../components/ReceiveModal'
import AccountAvatar from '../components/AccountAvatar'

export default function WalletScreen() {
  const { t } = useTranslation()
  const {
    activeAccount,
    balances,
    balancesLoading,
    resources,
    resourcesLoading,
    nfts,
    nftsLoading,
    refreshBalances,
    refreshResources,
    refreshNfts,
    navigate,
    setTransferTokenKey,
    setAvatar,
    clearAvatar,
    avatarStatus,
  } = useWalletStore()
  const {
    hideSmallBalances, setHideSmallBalances,
    manuallyHiddenTokens, hideToken, unhideToken,
    manuallyShownTokens, showToken, unshowToken,
  } = useAppStore()
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState<'tokens' | 'nfts' | 'history' | 'resources'>('tokens')
  const [accountSwitcherOpen, setAccountSwitcherOpen] = useState(false)
  const [receiveOpen, setReceiveOpen] = useState(false)
  const [sendNftTarget, setSendNftTarget] = useState<{ assetId: string; name: string } | null>(null)
  const [showHiddenTokens, setShowHiddenTokens] = useState(false)
  const nftsFetchedForAccount = useRef<string | null>(null)

  // ── All existing effects — UNCHANGED ───────────────────────────
  useEffect(() => { nftsFetchedForAccount.current = null }, [activeAccount?.name])

  useEffect(() => {
    const name = activeAccount?.name?.trim()
    if (!name) return
    refreshBalances()
    refreshResources()
    const interval = setInterval(() => {
      const n = useWalletStore.getState().activeAccount?.name?.trim()
      if (!n) return
      refreshBalances()
      refreshResources()
    }, 30_000)
    return () => clearInterval(interval)
  }, [activeAccount?.name, refreshBalances, refreshResources])

  useEffect(() => {
    const name = activeAccount?.name?.trim()
    if (!name) return
    if (activeTab !== 'nfts') return
    if (nftsFetchedForAccount.current === name) return
    nftsFetchedForAccount.current = name
    void refreshNfts()
  }, [activeTab, activeAccount?.name, refreshNfts])

  const handleCopyAddress = async () => {
    if (!activeAccount) return
    await navigator.clipboard.writeText(activeAccount.name)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // ── Derived values — UNCHANGED ─────────────────────────────────
  const accountTitle = activeAccount ? formatAccountDisplay(activeAccount) : ''
  const vaultaBalance = balances.find(b => b.symbol === 'A')
  const tokenKey = (b: { contract: string; symbol: string }) => `${b.contract}:${b.symbol}`
  /** Key format expected by TransferScreen / TokenPicker (double colon). */
  const tokenTransferKey = (b: { contract: string; symbol: string }) =>
    `${b.contract}::${b.symbol}`

  const nonZeroBalances = balances.filter(b => b.amountRaw > 0)

  /**
   * A token is hidden when the user hid it, or when its contract is not on the
   * verified list and the user has not pulled it out. Anyone can mint a token
   * named like a real one, so unverified tokens never appear on top by default.
   */
  const isHidden = (b: { contract: string; symbol: string; isKnown?: boolean }) => {
    const key = tokenKey(b)
    if (manuallyHiddenTokens.includes(key)) return true
    if (b.isKnown === false && !manuallyShownTokens.includes(key)) return true
    return false
  }
  const visibleBalances = nonZeroBalances.filter(b => !isHidden(b))
  const hiddenBalances = nonZeroBalances.filter(isHidden)
  const listSource = showHiddenTokens
    ? [...visibleBalances, ...hiddenBalances]
    : visibleBalances
  // Single source of truth for token order — see chain-eos/tokens-sort.ts.
  const sortedBalances = sortTokensForDisplay(
    hideSmallBalances ? listSource.filter(b => b.amountRaw > 0.001) : listSource,
  )
  const displayedBalances = sortedBalances
  const totalUsd = visibleBalances.reduce((sum, b) => sum + (b.usdValue ?? 0), 0)
  const hasPrices = visibleBalances.some(b => b.usdValue !== undefined)

  function fmtMicros(us: number): string {
    if (us < 1000) return `${us}μs`
    return `${(us / 1000).toFixed(2)}ms`
  }
  function fmtBytes(b: number): string {
    if (b < 1024) return `${b}B`
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(2)}KB`
    return `${(b / (1024 * 1024)).toFixed(2)}MB`
  }

  // ── JSX ─────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full" style={{ background: '#000000' }}>

      {/* ── Header ───────────────────────────────────────────────── */}
      <div
        className="pt-safe px-4 pb-5 flex-shrink-0"
        style={{ background: '#0c100c', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        {/* Account row */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <AccountAvatar avatarUrl={activeAccount?.avatarUrl} size="xs" />

            {/* Account name + switcher */}
            <button
              type="button"
              onClick={() => setAccountSwitcherOpen(true)}
              className="flex items-center gap-1.5 text-sm min-w-0 flex-1 text-left"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <div className="text-left min-w-0">
                <p className="text-white font-semibold text-sm leading-none truncate">{accountTitle}</p>
                <p className="text-xs mt-1" style={{ color: '#6b7280' }}>EOS Mainnet</p>
              </div>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                <polyline points="6,9 12,15 18,9"/>
              </svg>
            </button>
          </div>

          {/* Refresh */}
          <button
            onClick={() => refreshBalances()}
            className="flex-shrink-0"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#9ca3af' }}
            title="Refresh"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {balancesLoading
                ? <circle cx="12" cy="12" r="10" className="animate-spin" style={{ strokeDasharray: '30 60' }}/>
                : <>
                    <polyline points="23,4 23,10 17,10"/>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </>
              }
            </svg>
          </button>
        </div>

        {/* ── Balance block with radial glow ─────────────────────── */}
        <div className="text-center mb-5 glow-bg">
          <p className="section-label mb-1.5">{t('wallet.totalBalance')}</p>

          {balancesLoading && !vaultaBalance ? (
            <div className="h-12 flex items-center justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#43a047', borderTopColor: 'transparent' }} />
            </div>
          ) : (
            <>
              <p className="font-extrabold leading-none tracking-tight" style={{ fontSize: 40, color: '#fff', letterSpacing: '-0.02em' }}>
                {parseFloat(vaultaBalance?.amount ?? '0').toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4,
                })}
                <span className="font-semibold ml-2" style={{ fontSize: 22, color: '#5cbf63' }}>A</span>
              </p>
              {hasPrices && totalUsd > 0 ? (
                <p className="text-sm font-medium mt-1.5" style={{ color: '#4ade80' }}>{formatUsd(totalUsd)}</p>
              ) : (
                <p className="text-xs mt-1.5" style={{ color: '#6b7280' }}>Vaulta (core.vaulta)</p>
              )}
            </>
          )}

          {/* Address pill */}
          <button onClick={handleCopyAddress} className="address-btn mt-3 mx-auto" style={{ width: 'fit-content' }}>
            <span className="font-mono" style={{ fontSize: 12, color: '#9ca3af' }}>{activeAccount?.name}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={copied ? '#4ade80' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {copied
                ? <polyline points="20,6 9,17 4,12"/>
                : <>
                    <rect x="9" y="9" width="13" height="13" rx="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </>
              }
            </svg>
          </button>
        </div>

        {/* ── Action buttons ─────────────────────────────────────── */}
        <div className="flex gap-2.5">
          <button
            onClick={() => {
              setTransferTokenKey(null)
              navigate('transfer')
            }}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
            style={{ height: 46 }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22,2 15,22 11,13 2,9"/>
            </svg>
            {t('wallet.send')}
          </button>
          <button
            onClick={() => setReceiveOpen(true)}
            className="btn-secondary flex-1 flex items-center justify-center gap-2"
            style={{ height: 46 }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5cbf63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="7,16 12,21 17,16"/>
              <line x1="12" y1="21" x2="12" y2="3"/>
            </svg>
            {t('wallet.receive')}
          </button>
          <button
            onClick={() => navigate('messages')}
            className="btn-secondary flex items-center justify-center gap-2"
            style={{ height: 46, minWidth: 46, padding: '0 14px' }}
            title={t('mechat.title')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5cbf63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="flex flex-shrink-0" style={{ background: '#000000', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {(['tokens', 'nfts', 'history', 'resources'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`tab-underline${activeTab === tab ? ' active' : ''}`}
          >
            {tab === 'tokens'
              ? t('wallet.tokens')
              : tab === 'nfts'
                ? t('wallet.nfts')
                : tab === 'history'
                  ? t('wallet.history')
                  : t('wallet.resources')}
          </button>
        ))}
      </div>

      {/* ── Content ──────────────────────────────────────────────── */}
      <div className="scroll-area flex-1 px-4 py-3 space-y-2">
        {activeTab === 'tokens' && (
          <>
            {(nonZeroBalances.some(b => b.amountRaw > 0 && b.amountRaw <= 0.001) || hiddenBalances.length > 0) && (
              <div className="flex justify-end gap-3">
                {hiddenBalances.length > 0 && (
                  <button
                    onClick={() => setShowHiddenTokens(v => !v)}
                    className="text-xs transition-colors flex items-center gap-1"
                    style={{ color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {showHiddenTokens
                      ? t('wallet.hideUnknown')
                      : `${t('wallet.showAllTokens')} (+${hiddenBalances.length})`}
                  </button>
                )}
                {nonZeroBalances.some(b => b.amountRaw > 0 && b.amountRaw <= 0.001) && (
                  <button
                    onClick={() => setHideSmallBalances(!hideSmallBalances)}
                    className="text-xs transition-colors flex items-center gap-1"
                    style={{ color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {hideSmallBalances ? t('wallet.showAll') : t('wallet.hideSmall')}
                  </button>
                )}
              </div>
            )}
            {displayedBalances.map(token => {
              const key = tokenKey(token)
              const isManuallyHidden = manuallyHiddenTokens.includes(key)
              const isTokenHidden = isHidden(token)
              return (
                <TokenCard
                  key={key}
                  token={token}
                  highlighted={token.symbol === 'A'}
                  onSend={() => {
                    setTransferTokenKey(tokenTransferKey(token))
                    navigate('transfer')
                  }}
                  /*
                   * Swap is intentionally not wired up for the Play release.
                   * The button only opened WebDEX in the in-app browser — no
                   * exchange happens in the app and no fee is taken — but we
                   * would rather not argue that during review of the first
                   * production build. To bring it back, restore:
                   *
                   *   onSwap={() => {
                   *     setDiscoveryUrl(getWebdexSwapUrl(token))
                   *     navigate('discovery')
                   *   }}
                   *
                   * getWebdexSwapUrl and TokenCard's onSwap prop are unchanged.
                   */
                  onHide={
                    token.symbol === 'A'
                      ? undefined
                      : () => {
                          if (token.isKnown === false) {
                            // Unverified: the allowlist decides, not the hide list.
                            manuallyShownTokens.includes(key) ? unshowToken(key) : showToken(key)
                            return
                          }
                          isManuallyHidden ? unhideToken(key) : hideToken(key)
                        }
                  }
                  hideLabel={isTokenHidden ? '+' : '✕'}
                />
              )
            })}
            {displayedBalances.length === 0 && !balancesLoading && (
              <p className="text-center text-sm py-8" style={{ color: '#6b7280' }}>
                {t('wallet.noTokens')}
              </p>
            )}
          </>
        )}

        {activeTab === 'nfts' && (
          <div className="space-y-3">
            {/* The avatar lives in the `properavatar` contract, so other people
                see it too. Saying so matters: it is public, and it costs RAM. */}
            <div className="card px-3 py-2.5 flex items-center gap-3">
              <AccountAvatar avatarUrl={activeAccount?.avatarUrl} size="md" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white font-medium">{t('nft.avatarTitle')}</p>
                <p className="text-[11px] text-gray-500 leading-snug">
                  {avatarStatus?.state === 'saving'
                    ? t('nft.avatarSaving')
                    : avatarStatus?.state === 'saved'
                      ? t('nft.avatarSaved')
                      : avatarStatus?.state === 'error'
                        ? t('nft.avatarError')
                        : t('nft.avatarHint')}
                </p>
              </div>
              {activeAccount?.avatarAssetId && (
                <button
                  type="button"
                  onClick={() => void clearAvatar()}
                  disabled={avatarStatus?.state === 'saving'}
                  className="btn-ghost text-[11px] py-1.5 px-2.5 flex-shrink-0"
                >
                  {t('nft.removeAvatar')}
                </button>
              )}
            </div>

            <NftGallery
              nfts={nfts}
              nftsLoading={nftsLoading}
              selectedAssetId={activeAccount?.avatarAssetId ?? null}
              onSelectAvatar={(assetId, url) => void setAvatar(assetId, url)}
              onSend={(assetId, name) => setSendNftTarget({ assetId, name })}
            />
          </div>
        )}

        {sendNftTarget && (
          <NftSendModal
            assetId={sendNftTarget.assetId}
            nftName={sendNftTarget.name}
            onClose={() => setSendNftTarget(null)}
          />
        )}

        {activeTab === 'history' && <HistoryList account={activeAccount?.name ?? ''} />}

        {activeTab === 'resources' && (
          <div className="card space-y-4">
            {resourcesLoading && !resources ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#43a047', borderTopColor: 'transparent' }} />
              </div>
            ) : resources ? (
              <>
                <ResourceBar
                  label={t('wallet.cpu')}
                  percentUsed={resources.cpu.percentUsed}
                  usedLabel={fmtMicros(resources.cpu.used)}
                  availLabel={fmtMicros(resources.cpu.max)}
                />
                <ResourceBar
                  label={t('wallet.net')}
                  percentUsed={resources.net.percentUsed}
                  usedLabel={fmtBytes(resources.net.used)}
                  availLabel={fmtBytes(resources.net.max)}
                />
                <ResourceBar
                  label={t('wallet.ram')}
                  percentUsed={resources.ram.percentUsed}
                  usedLabel={fmtBytes(resources.ram.used)}
                  availLabel={fmtBytes(resources.ram.quota)}
                />
                <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-xs text-center mb-3" style={{ color: '#6b7280' }}>
                    ⚡ {t('resources.fuelAuto')}
                  </p>
                  <button
                    className="btn-secondary w-full text-sm"
                    style={{ height: 42 }}
                    onClick={() => navigate('resources')}
                  >
                    {t('resources.powerUp')} / {t('resources.buyRam')}
                  </button>
                </div>
              </>
            ) : (
              <p className="text-center text-sm py-8" style={{ color: '#6b7280' }}>
                {t('common.error')}
              </p>
            )}
          </div>
        )}
        <div style={{ height: 16 }} />
      </div>

      <AccountSwitcherModal open={accountSwitcherOpen} onClose={() => setAccountSwitcherOpen(false)} />
      {receiveOpen && <ReceiveModal onClose={() => setReceiveOpen(false)} />}
      <BottomNav />
    </div>
  )
}
